import {Hex} from 'viem';
import {DeliverableId} from '../../components/dex/dex-streaming/types/deliverable';
import {TradableId} from '../../components/dex/dex-streaming/types/tradable';
import {TrancheId} from '../../components/dex/dex-streaming/types/tranche';
import {SelectItem} from '../../components/select-input/select-item';
import {Side} from '../../enums/side';

export enum TxHistoryRecMethod {
    DEPOSIT  = 'DEPOSIT',
    WITHDRAW = 'WITHDRAW',
    TRANSFER = 'TRANSFER'
}

export interface TxHistoryMetaErrorInfo {
    msg: string;
    isL10n?: boolean;
    key?: string;
}

export interface TxHistoryMeta extends Record<string, any> {
    method: TxHistoryRecMethod;
    error?: TxHistoryMetaErrorInfo;
}

export enum TxHistoryStatus {
    PENDING   = 'PENDING',
    CONFIRMED = 'CONFIRMED',
    DONE      = 'DONE',
    FAILED    = 'FAILED'
}

export enum TxHistoryFailureReason {
    UNKNOWN  = 'UNKNOWN',
    REVERTED = 'REVERTED',
    TIMEOUT  = 'TIMEOUT'
}

export interface TxHistoryRec {
    hash: string;
    from: string;
    to: string | null;
    data: string;
    value: string; // in Wei
    nonce: number;
    gasLimit: string;
    gasPrice?: string;
    maxFeePerGas?: string;
    maxPriorityFeePerGas?: string;
    chainId: number;
    blockNumber?: string; // null while in mempool
    status: TxHistoryStatus;
    createdAt: number;
    updatedAt: number;
    metadata?: TxHistoryMeta;
    logs?: TxHistoryLog[];
    logsMeta?: TxHistoryDepositLogsMeta | TxHistoryWithdrawLogsMeta | TxHistoryTransferLogsMeta | unknown;
    gasUsedNative?: string; // Gas spent in native currency (in Wei)
    failureReason?: TxHistoryFailureReason;
}

export interface TxHistoryDepositMeta extends TxHistoryMeta {
    method: TxHistoryRecMethod.DEPOSIT;
    trancheId: TrancheId;
    deliverableId: DeliverableId;
    assetQtyToDeposit: string;
    minLptQty: string;
    clientReference: Hex;
}

export interface TxHistoryWithdrawMeta extends TxHistoryMeta {
    method: TxHistoryRecMethod.WITHDRAW;
    trancheId: TrancheId;
    lptQuantityToBurn: string;
    minAssetValidUsd: string;
    preferredDeliverableId: DeliverableId;
    minAcceptablePreferredAssetQty: string;
    clientReference: Hex;
}

export interface TxHistoryTransferMeta extends TxHistoryMeta {
    method: TxHistoryRecMethod.TRANSFER;
    deliverableId: DeliverableId;
    tokenAddress: string;
    amount: string; // as a float number
    fromWallet: string;
    toWallet: string;
}

export interface TxHistoryOrderLogsMeta {
    orderEnteredEvent: TxHistoryOrderEnteredEvent;
}

export interface TxHistoryDepositLogsMeta extends TxHistoryOrderLogsMeta {
    /**
     * @deprecated
     * Used before the "2-phase" release.
     * Replaced by orderEnteredEvent
     */
    depositEvents?: TxHistoryDepositEvent[];
}

export interface TxHistoryWithdrawLogsMeta extends TxHistoryOrderLogsMeta {

    /**
     * @deprecated
     * Used before the "2-phase" release.
     * Replaced by orderEnteredEvent
     */
    withdrawalEvents?: TxHistoryWithdrawEvent[];

    /**
     * @deprecated
     * Used before the "2-phase" release.
     * Replaced by orderEnteredEvent
     */
    lpTokenBurntEvents?: LiquidityPoolTokenBurntEvent[];
}

export interface TxHistoryTransferLogsMeta {
    transferEvents: TxHistoryTransferEvent[];
}

export interface TxHistoryDepositEvent {
    sent: TxHistoryDepositSentRec;
    received: TxHistoryDepositReceivedRec;
    feeUsd: string;
}

export interface TxHistoryOrderEnteredEvent {
    address: Hex;
    orderId: string;
    tradeableEntityId: TradableId;
    side: Side;
    deliverableId: DeliverableId;
    quantityDec: string;
}

export interface TxHistoryWithdrawEvent {
    lptPrice: string;
    lptQuantityToBurn: string;
    withdrawalFeeUsd: string;
    assetValueWithdrawnUsd: string;
    assetsWithdrawn: TxHistoryWithdrawAssetRec[];
}

export interface LiquidityPoolTokenBurntEvent {
    poolTrancheId: TrancheId;
    lptDeliverableId: DeliverableId;
    lptPrice: string;
    lptQuantityBurnt: string;
}

export interface TxHistoryTransferEvent {
    assetId: DeliverableId;
    amount: string;
    from: string;
    to: string;
    timestamp: string;
}

export interface TxHistoryWithdrawAssetRec {
    deliverableId: DeliverableId;
    assetPrice: string;
    assetQuantity: string;
    assetValue: string;
}

export interface TxHistoryDepositSentRec {
    deliverableId: DeliverableId;
    assetPrice: string;
    assetQuantity: string;
    assetValue: string;
}

export interface TxHistoryDepositReceivedRec {
    trancheId: TrancheId;
    qty: string;
    qtyUsd: string;
}

export const TX_HISTORY_REC_STATUS_NAMING: Record<TxHistoryStatus, string> = {
    [TxHistoryStatus.PENDING]:   'Pending', // Pending New - don't send to Backend
    [TxHistoryStatus.CONFIRMED]: 'Confirmed', // New Order, Order Cancel Request, Order Cancel Replace Request
    [TxHistoryStatus.DONE]:      'Done', // Confirmed -> Done => Exec.Report New, then Part.Fill/Fill/Cancel
    [TxHistoryStatus.FAILED]:    'Failed' // Confirmed -> Failed => Order Reject, Order Cancel Reject, Order Cancel Replace Reject
};

export const TX_HISTORY_REC_STATUS_SELECT_ITEMS: Array<SelectItem<TxHistoryStatus>> =
                 (Object.keys(TX_HISTORY_REC_STATUS_NAMING) as TxHistoryStatus[])
                     .map(value => ({
                         value,
                         label: TX_HISTORY_REC_STATUS_NAMING[value]
                     }));

// TODO: locale
export const TX_HISTORY_REC_METHOD_NAMING: Record<TxHistoryRecMethod, string> = {
    [TxHistoryRecMethod.DEPOSIT]:  'Purchase',
    [TxHistoryRecMethod.WITHDRAW]: 'Redeem',
    [TxHistoryRecMethod.TRANSFER]: 'Transfer'
};

export const TX_HISTORY_REC_METHOD_SELECT_ITEMS: Array<SelectItem<TxHistoryRecMethod>> =
                 (Object.keys(TX_HISTORY_REC_METHOD_NAMING) as TxHistoryRecMethod[])
                     .map(value => ({
                         value,
                         label: TX_HISTORY_REC_METHOD_NAMING[value]
                     }));

export interface TxHistoryLog {
    contract: string;
    event: string;
    args: Record<string, any>;
    index: number;
}
